import { useEffect, useState } from "react";
import { Box, Typography, Avatar, Button, Skeleton, Stack } from "@mui/material";
import ChemaDonationDialog from "../../../../shared-ui/ChemaDonationDialog";
import apiService from "../../../../../../../../../service/Api";
import bgImage from "./background.jpeg";

interface DonationEntry {
  id: number;
  display_name: string | null;
  donor_name: string | null;
  amount: string;
  currency: string;
  paid: boolean;
  created_at: string;
}

interface DonationSideCardProps {
  streamId: number;
  streamTitle: string;
  deceasedImage?: string;
  refreshKey?: number;
  onDonationSuccess?: () => void;
}

function initials(name: string | null): string {
  if (!name) return "?";
  return name
    .trim()
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function DonationSideCard({
  streamId,
  streamTitle,
  deceasedImage,
  refreshKey = 0,
  onDonationSuccess,
}: DonationSideCardProps) {
  const [donations, setDonations] = useState<DonationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await apiService.get<
          DonationEntry[] | { results: DonationEntry[] }
        >("/api/chema/donations/", {
          stream_id: streamId,
          paid: "true",
          ordering: "-created_at",
        });
        if (cancelled) return;
        const data = res.data;
        setDonations(Array.isArray(data) ? data : (data?.results ?? []));
      } catch {
        if (!cancelled) setDonations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [streamId, refreshKey]);

  const total = donations.reduce(
    (sum, d) => sum + parseFloat(d.amount || "0"),
    0,
  );
  const totalFormatted = total.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  const latest = donations[0] ?? null;
  const latestName = latest?.display_name || latest?.donor_name || "Anonymous";
  const latestAmount = latest
    ? `$${parseFloat(latest.amount).toFixed(0)}`
    : null;

  const recentDonations = donations.slice(0, 5);

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <>
      <Box
        sx={{
          width: 200,
          minWidth: 250,

          borderRadius: "18px",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          flexShrink: 0,
        }}
      >
        {/* Background image */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.55)",
          }}
        />

        {/* Content above the image */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 2,
          }}
        >
          {/* Recent donor notification */}
          {latest && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "rgba(255,255,255,0.95)",
                borderRadius: "10px",
                p: "6px 10px",
                mb: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: "0.65rem",
                  bgcolor: "#1d7a6a",
                }}
              >
                {initials(latestName)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Chema Update
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "text.primary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {latestName} donated {latestAmount}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Total */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: "rgba(255,255,255,0.75)",
                textTransform: "uppercase",
                letterSpacing: 1,
                mb: 0.5,
              }}
            >
              Chema Donated
            </Typography>
            {loading ? (
              <Skeleton
                variant="text"
                width={120}
                height={52}
                sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: "2.2rem",
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: 1,
                }}
              >
                {totalFormatted}
              </Typography>
            )}
          </Box>

          {/* Donate button */}
          <Button
            fullWidth
            onClick={() => setDialogOpen(true)}
            sx={{
              bgcolor: "#1d7a6a",
              color: "#fff",
              borderRadius: "10px",
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: 1,
              py: 1.2,
              "&:hover": { bgcolor: "#155f52" },
            }}
          >
            Donate Chema
          </Button>
        </Box>
      </Box>

      {!loading && recentDonations.length > 0 && (
        <Box
          sx={{
            mt: 2,
            minWidth: 250,
            width: 200,
            borderRadius: "18px",
            bgcolor: "background.paper",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 1,
              mb: 1.5,
              fontWeight: 700,
            }}
          >
            Recent Donations
          </Typography>
          <Stack spacing={1.25}>
            {recentDonations.map((donation) => {
              const name =
                donation.display_name || donation.donor_name || "Anonymous";
              return (
                <Box
                  key={donation.id}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.65rem",
                      bgcolor: "#1d7a6a",
                    }}
                  >
                    {initials(name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                    >
                      {formatRelativeDate(donation.created_at)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "text.primary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {donation.currency === "ZWL" ? "ZWL" : "$"}
                    {parseFloat(donation.amount || "0").toFixed(0)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      <ChemaDonationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        streamId={streamId}
        streamTitle={streamTitle}
        deceasedImage={deceasedImage}
        onDonationSuccess={() => {
          setDialogOpen(false);
          onDonationSuccess?.();
        }}
      />
    </>
  );
}
